import { Button } from "@/components/ui/button";
import { ShoppingCartSimple, Trash } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { variantAutoPrice } from "@/lib/cartPricing";

function CartGridCard({ item, onRemove, coefficient = 1, pricePerSkid = 0, showGtSku = false }) {
  const src = `${API}/cart/items/${item.id}/photo`;
  const category = (item.category || "").trim();
  const variants = item.variants || [];

  return (
    <div
      className="flex flex-col overflow-hidden rounded-sm border border-zinc-300 bg-white transition-colors duration-150 hover:border-blue-600"
      data-testid={`cart-grid-card-${item.id}`}
    >
      <div className="relative flex aspect-square items-center justify-center border-b border-zinc-200 bg-zinc-50">
        <img
          src={src}
          alt="thumbnail"
          className="h-[65%] w-[65%] object-contain"
          data-testid={`cart-grid-photo-${item.id}`}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        <Button
          variant="outline"
          size="sm"
          className="absolute right-1.5 top-1.5 h-auto rounded-sm border-red-300 bg-white/90 p-1.5 text-red-600 hover:bg-red-50"
          onClick={() => onRemove(item.id)}
          data-testid={`cart-grid-remove-button-${item.id}`}
        >
          <Trash size={14} />
        </Button>
        {category && (
          <span className="absolute left-1.5 top-1.5 rounded-sm bg-white/90 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.05em] text-zinc-500">
            {category}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-2">
        <div className="truncate text-sm font-bold text-zinc-900" data-testid={`cart-grid-item-no-${item.id}`}>
          {item.item_no || "-"}
        </div>
        <div className="line-clamp-2 text-xs text-zinc-600" data-testid={`cart-grid-description-${item.id}`}>
          {item.description || "-"}
        </div>
        {variants.length > 0 && (
          <table className="mt-1 w-full border-t border-zinc-200 text-[11px]" data-testid={`cart-grid-variants-${item.id}`}>
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.03em] text-zinc-400">
                <th className="pt-1 text-left font-normal">Size</th>
                {showGtSku && <th className="pt-1 text-left font-normal">GT SKU</th>}
                <th className="pt-1 text-right font-normal">QTY</th>
                <th className="pt-1 text-right font-normal">Price</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v, idx) => {
                const price = v.final_price != null ? Number(v.final_price) : variantAutoPrice(v, coefficient, pricePerSkid);
                return (
                  <tr key={idx} className="border-t border-zinc-100 text-zinc-700">
                    <td className="py-0.5">{(v.size || "").trim() || "-"}</td>
                    {showGtSku && <td className="py-0.5 font-mono-data">{v.gt_sku || "-"}</td>}
                    <td className="py-0.5 text-right font-mono-data">
                      {v.inventory_qty != null && v.inventory_qty !== "" ? Math.round(v.inventory_qty) : "-"}
                    </td>
                    <td className="py-0.5 text-right font-mono-data">${price.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function CartItemsGrid({ items, onRemove, coefficient = 1, pricePerSkid = 0, showGtSku = false }) {
  if (items.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-zinc-400" data-testid="cart-empty-state">
        <ShoppingCartSimple size={32} />
        <p className="text-sm">Your cart is empty. Add products from the main list.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7" data-testid="cart-grid">
      {items.map((item) => (
        <CartGridCard key={item.id} item={item} onRemove={onRemove} coefficient={coefficient} pricePerSkid={pricePerSkid} showGtSku={showGtSku} />
      ))}
    </div>
  );
}
